
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Field, Input, Select } from "@client/shared/design";
import { cx } from "@client/shared/lib/cx.ts";
import { compose, hasStructured, type AddressComponent } from "@shared/listing";
import {
  fetchPlaceSuggestions,
  fetchPlaceDetails,
  newPlacesSessionToken,
  type PlaceSuggestion,
  type PlaceDetails,
} from "./places-client.ts";
import type { ListingAddressDraft } from "./use-new-listing-form.ts";
import styles from "./NewListing.module.css";

export interface AddressFieldsProps {
  value: ListingAddressDraft;
  error?: string;
  onChange: (next: ListingAddressDraft) => void;
  onBlur?: () => void;
}

const DEBOUNCE_MS = 220;


function browserTimeZones(): string[] {
  const supported = (Intl as { supportedValuesOf?: (key: string) => string[] })
    .supportedValuesOf;
  if (typeof supported !== "function") return [];
  try {
    return supported("timeZone");
  } catch {
    return [];
  }
}

type Mode = "search" | "manual";

export function AddressFields({
  value,
  error,
  onChange,
  onBlur,
}: AddressFieldsProps) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>(() =>
    !value.placeId && hasStructured(value) ? "manual" : "search",
  );
  const sessionTokenRef = useRef<string>(newPlacesSessionToken());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      fetchPlaceSuggestions(q, sessionTokenRef.current, ac.signal)
        .then((results) => {
          setSuggestions(results);
          setOpen(results.length > 0);
          setActive(-1);
        })
        .catch(() => {
          if (ac.signal.aborted) return;
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const choose = useCallback(
    async (suggestion: PlaceSuggestion) => {
      setOpen(false);
      setLoading(true);
      try {
        const details: PlaceDetails = await fetchPlaceDetails(
          suggestion.placeId,
          sessionTokenRef.current,
        );
        onChange(details);
        setQuery("");
        setMode("search");
        sessionTokenRef.current = newPlacesSessionToken();
      } catch {
      } finally {
        setLoading(false);
      }
    },
    [onChange],
  );

  const editComponent = useCallback(
    (key: AddressComponent, text: string) => {
      const next: ListingAddressDraft = { ...value };
      const components = next as Record<AddressComponent, string | undefined>;
      if (text === "") delete components[key];
      else components[key] = text;
      delete next.placeId;
      delete next.lat;
      delete next.lng;
      next.formatted = compose(next);
      onChange(next);
    },
    [value, onChange],
  );

  const openManual = useCallback(() => {
    const typed = query.trim();
    if (typed !== "" && !value.line1) editComponent("line1", typed);
    setOpen(false);
    setMode("manual");
  }, [query, value.line1, editComponent]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      const chosen = suggestions[active];
      if (chosen) void choose(chosen);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const tzOptions = useMemo(() => {
    const base = browserTimeZones();
    const current = value.timezone;
    if (current && !base.includes(current)) return [current, ...base];
    return base;
  }, [value.timezone]);

  if (mode === "manual") {
    return (
      <div className={styles.addressGrid}>
        <Field label="Address line 1" className={styles.addressSpan} error={error}>
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="address-line1"
              placeholder="123 Maple Street"
              value={value.line1 ?? ""}
              onChange={(e) => editComponent("line1", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field label="Address line 2" optional className={styles.addressSpan}>
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="address-line2"
              placeholder="Apt, suite, unit"
              value={value.line2 ?? ""}
              onChange={(e) => editComponent("line2", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field label="City">
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="address-level2"
              placeholder="Springfield"
              value={value.city ?? ""}
              onChange={(e) => editComponent("city", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field label="State / Region">
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="address-level1"
              placeholder="IL"
              value={value.region ?? ""}
              onChange={(e) => editComponent("region", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field label="Postal code">
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="postal-code"
              placeholder="62704"
              value={value.postalCode ?? ""}
              onChange={(e) => editComponent("postalCode", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field label="Country">
          {(wiring) => (
            <Input
              {...wiring}
              autoComplete="country-name"
              placeholder="United States"
              value={value.country ?? ""}
              onChange={(e) => editComponent("country", e.target.value)}
              onBlur={onBlur}
            />
          )}
        </Field>

        <Field
          label="Time zone"
          className={styles.addressSpan}
          hint="Sets the listing’s zone: agents always see times in their own zone, computed from this."
        >
          {(wiring) => (
            <Select
              {...wiring}
              value={value.timezone ?? ""}
              onChange={(e) => onChange({ ...value, timezone: e.target.value })}
              onBlur={onBlur}
            >
              {!value.timezone && (
                <option value="" disabled>
                  Select a time zone…
                </option>
              )}
              {tzOptions.map((zone) => (
                <option key={zone} value={zone}>
                  {zone.replace(/_/g, " ")}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    );
  }

  if (value.placeId) {
    const locality = [value.city, value.region].filter(Boolean).join(", ");
    const secondary = [locality, value.postalCode].filter(Boolean).join(" ");
    return (
      <div className={styles.confirmedCard}>
        <span className={styles.confirmedIcon} aria-hidden="true">
          <CheckMark />
        </span>
        <div className={styles.confirmedBody}>
          <span className={styles.confirmedPrimary}>
            {value.line1 ?? value.formatted}
          </span>
          {(secondary || value.country) && (
            <span className={styles.confirmedSecondary}>
              {[secondary, value.country].filter(Boolean).join(" · ")}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={styles.confirmedEdit}
          onClick={() => setMode("manual")}
        >
          Edit
        </Button>
      </div>
    );
  }

  return (
    <Field label="Property address" error={error}>
      {(wiring) => (
        <>
          <div className={styles.autocomplete}>
            <span className={styles.searchIcon} aria-hidden="true">
              <SearchIcon />
            </span>
            <Input
              {...wiring}
              className={styles.searchInput}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && active >= 0 ? `${listId}-opt-${active}` : undefined
              }
              autoComplete="off"
              placeholder="Search 123 Maple Street, Springfield…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onKeyDown={onKeyDown}
              onBlur={() => {
                setTimeout(() => setOpen(false), 120);
                onBlur?.();
              }}
            />

            {loading && (
              <span className={styles.searchSpinner} aria-hidden="true" />
            )}

            {open && suggestions.length > 0 && (
              <ul id={listId} role="listbox" className={styles.suggestions}>
                {suggestions.map((s, i) => (
                  <li
                    key={s.placeId}
                    id={`${listId}-opt-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className={cx(
                      styles.suggestion,
                      i === active && styles.suggestionActive,
                    )}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      void choose(s);
                    }}
                  >
                    <span className={styles.suggestionPrimary}>
                      {s.primary ?? s.description}
                    </span>
                    {s.secondary && (
                      <span className={styles.suggestionSecondary}>
                        {s.secondary}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className={styles.manualPrompt}>
            We’ll fill in the city, ZIP and time zone for you. Or{" "}
            <button
              type="button"
              className={styles.linkButton}
              onClick={openManual}
            >
              enter the address manually
            </button>
            .
          </p>
        </>
      )}
    </Field>
  );
}

function CheckMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.4 6.5 11.4 12.5 4.6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="m10.5 10.5 3 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
